import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { isThlNurse } from 'utils/thlNurseRoles';
import { IS_NON_PROD } from '../../../environment';
import TimezoneSelect from './TimezoneSelect';

let adminRoutes = [
  {
    url: '/admin',
    title: <FormattedMessage defaultMessage="Dashboard" />,
    icon: '../../../images/dashboard-navigation/dashboardicon.svg',
    key: 'dashboard',
  },
  {
    url: '/admin/practitioner_v2/1',
    title: <FormattedMessage defaultMessage="Practitioner V2 (NEW)" />,
    icon: '../../../images/dashboard-navigation/practitioner.svg',
    key: 'practitioner_v2',
  },
  {
    url: '/admin/practitioner',
    title: <FormattedMessage defaultMessage="Practitioner" />,
    icon: '../../../images/dashboard-navigation/practitioner.svg',
    key: 'practitioner',
  },
  {
    url: '/admin/specialized_practitioner',
    title: <FormattedMessage defaultMessage="Specialized Practitioner" />,
    icon: '../../../images/dashboard-navigation/practitioner.svg',
    key: 'specialized_practitioner',
  },
  {
    url: '/admin/clients',
    title: <FormattedMessage defaultMessage="Clients" />,
    icon: '../../../images/dashboard-navigation/clientsicon.svg',
    key: 'admin_clients',
  },
  {
    url: '/admin/appointments',
    title: <FormattedMessage defaultMessage="Appointments" />,
    icon: '../../../images/dashboard-navigation/scheduleicon.svg',
    key: 'appointments',
  },
  {
    url: '/admin/reports/clinical',
    title: <FormattedMessage defaultMessage="Reports" />,
    icon: '../../../images/dashboard-navigation/reportsicon.svg',
    key: 'reports',
  },
  {
    url: '/admin/provider_payouts',
    title: <FormattedMessage defaultMessage="Payouts" />,
    icon: '../../../images/dashboard-navigation/processicon.svg',
    key: 'payouts',
  },
  {
    url: '/admin/failed_payments',
    title: <FormattedMessage defaultMessage="Failed Payments" />,
    icon: '../../../images/dashboard-navigation/processicon.svg',
    key: 'failed_payments',
  },
  {
    url: '/admin/manual_payments',
    title: <FormattedMessage defaultMessage="Manual Payments" />,
    icon: '../../../images/dashboard-navigation/processicon.svg',
    key: 'manual_payments',
  },
  {
    url: '/admin/clinics',
    title: <FormattedMessage defaultMessage="Clinics" />,
    icon: '../../../images/dashboard-navigation/practitioner.svg',
    key: 'clinics',
  },
  {
    url: '/admin/admins',
    title: <FormattedMessage defaultMessage="Admins" />,
    icon: '../../../images/dashboard-navigation/practitioner.svg',
    key: 'admins',
  },
];

const practiceManagementRoutes = [
  {
    title: <FormattedMessage defaultMessage="Dashboard" />,
    icon: '../../../images/dashboard-navigation/dashboardicon.svg',
    url: '/dashboard',
    key: 'dashboard',
  },
  {
    url: '/schedule',
    title: <FormattedMessage defaultMessage="Schedule" />,
    icon: '../../../images/dashboard-navigation/scheduleicon.svg',
    key: 'schedule',
  },
  {
    url: '/clients',
    title: <FormattedMessage defaultMessage="Clients" />,
    icon: '../../../images/dashboard-navigation/clientsicon.svg',
    key: 'clients',
  },
  {
    url: '/session_notes/documents',
    title: <FormattedMessage defaultMessage="Session Notes" />,
    icon: '../../../images/dashboard-navigation/session_notes.svg',
    key: 'session_notes',
  },
  {
    url: '/shared_files/files_from_clients',
    title: <FormattedMessage defaultMessage="Shared Files" />,
    icon: '../../../images/dashboard-navigation/shared_files.svg',
    key: 'shared_files',
  },
];

let accountSettingsRoutes = [
  {
    url: '/preferences/account_details',
    title: <FormattedMessage defaultMessage="Preferences" />,
    icon: '../../../images/dashboard-navigation/preferencesicon.svg',
    key: 'preferences',
  },
  {
    url: '/payouts',
    title: <FormattedMessage defaultMessage="Payouts" />,
    icon: '../../../images/dashboard-navigation/payouts.svg',
    key: 'payouts',
  },
];

const drawerWidth = 240;

const selectedListItemSx = {
  '&.Mui-selected': {
    backgroundColor: 'rgba(144,243,168,.1) !important',
    borderLeft: 'white 4px solid !important',
  },
};

const primaryTextSx = {
  '& .MuiListItemText-primary': {
    fontFamily: 'Source Sans Pro',
    color: '#BDCAE5',
    fontSize: '14px',
    fontWeight: 600,
  },
};

const activeTextSx = {
  '& .MuiListItemText-primary': {
    fontFamily: 'Source Sans Pro',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
  },
};

const bottomNavigationSx = {
  height: '220px',
  position: 'relative',
  backgroundColor: '#252A67',
  width: '100%',
  borderTop: 'solid white',
  marginTop: '30px',
};

const Heading = styled('h1')({
  color: 'white',
  fontSize: '16px',
  fontWeight: '400',
  paddingLeft: 30,
  '& strong': {
    fontSize: '18px',
  },
});

function SideBar({ user }) {
  const belongToClinic = !!_.get(user, 'clinic_counsellor_profile');
  const superAdmin = _.get(user, 'roles', []).find(
    role => role.role_name === 'Super Admin',
  );

  if (!superAdmin && !IS_NON_PROD) {
    adminRoutes = adminRoutes.filter(item => item.key !== 'admins');
  }

  if (belongToClinic || isThlNurse(_.get(user, 'roles', []))) {
    accountSettingsRoutes = accountSettingsRoutes.filter(
      route => route.key !== 'payouts',
    );
  }

  const supportRoutes = [
    {
      url:
        user.locale === 'en'
          ? 'https://greenshieldhealthpractitioner.zendesk.com/hc/en-ca'
          : 'https://greenshieldhealthpractitioner.zendesk.com/hc/fr-ca',
      title: <FormattedMessage defaultMessage="Knowledge Base" />,
      icon: '../../../images/dashboard-navigation/knowledgebase.svg',
      newTab: true,
    },
    {
      url: '/support',
      title: <FormattedMessage defaultMessage="Support" />,
      icon: '../../../images/dashboard-navigation/supporticon.svg',
      key: 'support',
    },
  ];
  const renderRoutes = routes => (
    <List style={{ paddingTop: 0, paddingBottom: 0 }}>
      {routes.map(route => {
        const isExternal = route.url.startsWith('http');
        const isActive = window.location.pathname.split('/')[1] === route.key;

        return isExternal ? (
          <a
            key={route.url}
            href={route.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <ListItemButton disableGutters sx={selectedListItemSx}>
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingLeft: 30,
                }}
              >
                <ListItemIcon
                  style={{
                    minWidth: '40px',
                    width: '16px',
                    height: '16px',
                  }}
                >
                  <img src={route.icon} alt={route.title} />
                </ListItemIcon>
                <ListItemText primary={route.title} sx={primaryTextSx} />
              </Box>
            </ListItemButton>
          </a>
        ) : (
          <Link
            key={route.url}
            to={route.url}
            style={{ textDecoration: 'none' }}
          >
            <ListItemButton
              disableGutters
              selected={isActive}
              sx={selectedListItemSx}
            >
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingLeft: 30,
                }}
              >
                <ListItemIcon
                  style={{
                    minWidth: '40px',
                    width: '16px',
                    height: '16px',
                  }}
                >
                  <img src={route.icon} alt={route.title} />
                </ListItemIcon>
                <ListItemText
                  primary={route.title}
                  sx={isActive ? activeTextSx : primaryTextSx}
                />
              </Box>
            </ListItemButton>
          </Link>
        );
      })}
    </List>
  );

  return (
    <div style={{ display: 'flex' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            paddingTop: '120px',
            width: drawerWidth,
            backgroundColor: '#252A67',
          },
        }}
      >
        {window.location.pathname.split('/')[1] === 'admin' ? (
          <Box
            component="main"
            style={{
              overflow: 'hidden',
            }}
          >
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Box style={{ height: '100vh', overflow: 'auto' }}>
                <Heading>
                  <FormattedMessage
                    defaultMessage="Admin {linebreak}<strong>Management</strong>"
                    values={{
                      strong: chunks => <strong>{chunks}</strong>,
                      linebreak: <br />,
                    }}
                    components={{
                      strong: <strong />,
                    }}
                  />
                </Heading>
                {renderRoutes(adminRoutes)}
              </Box>
              <Box sx={bottomNavigationSx}>
                <Box style={{ paddingLeft: '30px' }}>
                  <TimezoneSelect user={user} />
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            component="main"
            style={{
              overflow: 'hidden',
            }}
          >
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Box style={{ height: '100vh', overflow: 'auto' }}>
                <Heading>
                  <FormattedMessage
                    defaultMessage="Practitioner {linebreak}<strong>Portal</strong>"
                    values={{
                      strong: chunks => <strong>{chunks}</strong>,
                      linebreak: <br />,
                    }}
                    components={{
                      strong: <strong />,
                    }}
                  />
                </Heading>
                {renderRoutes(practiceManagementRoutes)}
                <Heading style={{ paddingTop: '40px' }}>
                  <FormattedMessage defaultMessage="Account Settings" />
                </Heading>
                {renderRoutes(accountSettingsRoutes)}
              </Box>
              <Box sx={bottomNavigationSx}>
                <Box
                  style={{
                    paddingLeft: '30px',
                  }}
                >
                  <TimezoneSelect user={user} />
                </Box>
                <Box
                  style={{
                    paddingBottom: '30px',
                  }}
                >
                  {renderRoutes(supportRoutes)}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>
    </div>
  );
}

SideBar.propTypes = {
  user: PropTypes.object.isRequired,
};

export default SideBar;
